import React, {
	useState,
	useEffect,
	createContext,
	useContext,
	useCallback,
	useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser, loginWithTelegram } from "../router/apiServiceFetch";
import { useTelegram } from "../hooks/useTelegram";

const AuthContext = createContext({
	userBase: null,
	setUserBase: () => {},
	userPic: null,
	loading: true,
	isReady: false,
	reinit: async () => {},
});

export const AuthProvider = ({ children }) => {
	const navigate = useNavigate();
	const location = useLocation();

	const viewRate = useMemo(() => {
		const urlParams = new URLSearchParams(location.search);
		return urlParams.get("viewRate");
	}, [location.search]);

	const [userBase, setUserBase] = useState(null);
	const [userPic, setUserPic] = useState(null);
	const [loading, setLoading] = useState(true);
	const [initialized, setInitialized] = useState(false);

	const { tg, user } = useTelegram();

	const getTelegramInitData = useCallback(() => {
		const raw = window?.Telegram?.WebApp?.initData;
		return typeof raw === "string" ? raw : "";
	}, []);

	const initializeTelegram = useCallback(() => {
		if (!tg) return;

		const version = tg.version || "6.0";

		if (parseFloat(version) >= 6.1) {
			tg.setHeaderColor("#1F0030");
			tg.setBackgroundColor("#fff");
			tg.setBottomBarColor("#fff");
		}

		tg.MainButton.setParams({
			color: "#1F0030",
			textColor: "#FFFFFF",
		});
		tg.MainButton.hide();
		tg.BackButton.hide();

		tg.ready();
		tg.expand();

		if (parseFloat(version) >= 6.1) {
			tg.disableVerticalSwipes();
		}

		try {
			tg.lockOrientation();
		} catch {}

		const platform = tg.platform;
		const canFullscreen = platform !== "tdesktop" && parseFloat(version) >= 6.1;

		if (canFullscreen) {
			try {
				tg.disableVerticalSwipes();
				tg.requestFullscreen();
			} catch {}
		} else {
			document.documentElement.style.setProperty("--tg-top", "0");
			document.documentElement.style.setProperty("--tg-buttom", "127px");
		}
	}, [tg]);

	const initializeUser = useCallback(async () => {
		if (!user?.id) return;

		setLoading(true);

		try {
			setUserPic(user?.photo_url || "");

			const initData = getTelegramInitData();

			if (!initData) {
				window.location.href = "https://t.me/NarekNexus_bot";
				return;
			}

			const loginResult = await loginWithTelegram(initData);

			if (loginResult?.code === 500) {
				navigate("/block", { replace: true });
				return;
			}

			const userResponse = await getUser(user.id);

			if (!userResponse) {
				console.error("User not found.");
				navigate("/error", { replace: true });
				return;
			}

			setUserBase(userResponse);

			if (viewRate) {
				navigate(`${location.pathname}?viewRate=1`, { replace: true });
				return;
			}

			// const currentQuery = new URLSearchParams(window.location.search);

			// const startParam =
			// 	currentQuery.get("start_param") ||
			// 	currentQuery.get("tgWebAppStartParam") ||
			// 	tg?.initDataUnsafe?.start_param ||
			// 	"";

			// const consumedStartParam = sessionStorage.getItem("consumedStartParam");

			// if (startParam && consumedStartParam !== startParam) {
			// 	sessionStorage.setItem("consumedStartParam", startParam);

			// 	navigate(`/home?start_param=${encodeURIComponent(startParam)}`, {
			// 		replace: true,
			// 	});
			// 	return;
			// }

			navigate(`/home?v=${Date.now()}`, { replace: true });
		} catch (error) {
			console.error("Error during user initialization:", error);
			navigate("/error", { replace: true });
		} finally {
			setLoading(false);
		}
	}, [user, tg, getTelegramInitData, navigate, viewRate, location.pathname]);

	useEffect(() => {
		if (!tg || !user?.id) return;
		if (initialized) return;

		initializeTelegram();
		initializeUser();
		setInitialized(true);
	}, [tg, user, initialized, initializeTelegram, initializeUser]);

	const reinit = useCallback(async () => {
		await initializeUser();
	}, [initializeUser]);

	const isReady = !!userBase && !loading;
	const allowWithoutAuth = location.pathname === "/block";

	return (
		<AuthContext.Provider
			value={{
				userBase,
				setUserBase,
				userPic,
				loading,
				isReady,
				reinit,
			}}
		>
			{isReady || allowWithoutAuth ? children : null}
		</AuthContext.Provider>
	);
};

export const useAuth = () => useContext(AuthContext);
